/**
 * Documents filed against a patient, under categories.
 *
 * "نماذج التأمين الطبي" is the default: the insurance medical forms that say
 * which of the hospital's services a cover discounts or pays in full. Any other
 * category is whatever the uploader types, and the list offered next time is
 * every category this hospital has used — the vocabulary grows by use.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Icon, Input, Select, Spinner } from '../../components/common';
import { patientsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { formatDateTime, readFileAsDataUrl } from './labels';

const DEFAULT_CATEGORY = 'نماذج التأمين الطبي';
const MAX_BYTES = 8 * 1024 * 1024;

export function AttachmentsPanel({ patientId, canUpload = true }) {
  const { success, error: toastError } = useNotifications();
  const [rows, setRows] = useState(null);
  const [categories, setCategories] = useState([DEFAULT_CATEGORY]);
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [newCategory, setNewCategory] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const input = useRef(null);

  const load = async () => {
    setError(null);
    try {
      const [list, cats] = await Promise.all([patientsApi.attachments(patientId), patientsApi.categories()]);
      setRows(list);
      setCategories(cats && cats.length ? cats : [DEFAULT_CATEGORY]);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toastError('حجم الملف أكبر من 8 ميجابايت');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      await patientsApi.upload(patientId, {
        category: (category === '__new__' ? newCategory : category).trim() || DEFAULT_CATEGORY,
        title: title.trim() || file.name,
        fileName: file.name,
        dataUrl,
      });
      setTitle('');
      setNewCategory('');
      if (category === '__new__') setCategory(DEFAULT_CATEGORY);
      success('تم رفع المرفق');
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const open = async (row) => {
    try {
      const full = await patientsApi.attachment(row.id);
      const win = window.open();
      if (!win) return;
      win.document.title = full.title || full.fileName || 'مرفق';
      if (String(full.mimeType).startsWith('image/')) {
        win.document.body.innerHTML = `<img src="${full.dataUrl}" style="max-width:100%">`;
      } else {
        win.location.href = full.dataUrl;
      }
    } catch (err) {
      toastError(err.message);
    }
  };

  const remove = async (row) => {
    try {
      await patientsApi.removeAttachment(row.id);
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!rows) return <Spinner label="جارٍ تحميل المرفقات" variant="inline" />;

  const grouped = rows.reduce((map, row) => {
    (map[row.category] = map[row.category] || []).push(row);
    return map;
  }, {});

  return (
    <div className="space-y-5">
      {canUpload && (
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="التصنيف">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              <option value="__new__">+ تصنيف جديد…</option>
            </Select>
            {category === '__new__' && (
              <Input className="mt-2" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="اسم التصنيف" />
            )}
          </Field>
          <Field label="عنوان المستند" hint="اختياري">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: نموذج موافقة التأمين" />
          </Field>
          <div className="flex items-end">
            <input ref={input} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} />
            <Button icon="upload-cloud" loading={busy} onClick={() => input.current?.click()}>
              رفع ملف
            </Button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState icon="paperclip" title="لا توجد مرفقات" message="ارفع نماذج التأمين أو صور الأوراق الطبية." />
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="folder-open" size={15} className="text-amber-400" /> {cat}
              <Badge className="border-white/10 bg-white/5 text-slate-300">{list.length}</Badge>
            </h4>
            <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
              {list.map((row) => (
                <li key={row.id} className="flex items-center gap-3 p-3">
                  <Icon name={String(row.mimeType).includes('pdf') ? 'file-text' : 'image'} size={18} className="text-cyan-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-white">{row.title || row.fileName}</span>
                    <span className="block text-[11px] text-slate-500">{formatDateTime(row.createdAt)}</span>
                  </span>
                  <Button variant="subtle" icon="eye" onClick={() => open(row)}>
                    عرض
                  </Button>
                  {canUpload && (
                    <Button variant="subtle" icon="trash-2" onClick={() => remove(row)} aria-label="حذف المرفق" />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

export default AttachmentsPanel;
